import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  motion,
  AnimatePresence,
  useScroll,
  useTransform,
} from "framer-motion";
import { Menu, X, Home } from "lucide-react";
import { REGISTER_URL } from "../../config/constants";
import { Calendar, ArrowUpRight } from "lucide-react";

const LOGO_URL = "/cost-logo.png";

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { scrollY } = useScroll();

  const navBackground = useTransform(
    scrollY,
    [0, 100],
    ["rgba(3, 5, 8, 0)", "rgba(3, 5, 8, 0.95)"],
  );

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { name: "Home", href: "/", icon: Home },
    { name: "Features", href: "/features" },
    { name: "Solutions", href: "/solutions" },
    { name: "AI", href: "/ai-capabilities" },
    // { name: "Pricing", href: "/pricing" },
  ];

  return (
    <>
      <motion.nav
        style={{ backgroundColor: navBackground }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled ? "py-4 backdrop-blur-xl border-b border-white/5" : "py-6"
        }`}
        data-testid="navbar"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link
              to="/"
              className="flex items-center group"
              data-testid="navbar-logo"
            >
              <motion.img
                src={LOGO_URL}
                alt="Cost Genie"
                className="h-10 w-auto"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.3 }}
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className="relative px-5 py-2 group flex items-center gap-1.5"
                  data-testid={`nav-link-${link.name.toLowerCase()}`}
                >
                  {link.icon && (
                    <link.icon
                      className={`w-4 h-4 transition-colors duration-300 ${
                        location.pathname === link.href
                          ? "text-[#00D4FF]"
                          : "text-white/50 group-hover:text-white"
                      }`}
                    />
                  )}
                  <span
                    className={`text-sm tracking-wide transition-colors duration-300 ${
                      location.pathname === link.href
                        ? "text-[#00D4FF]"
                        : "text-white/60 group-hover:text-white"
                    }`}
                    style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                  >
                    {link.name}
                  </span>
                  {location.pathname === link.href && (
                    <motion.div
                      layoutId="navIndicator"
                      className="absolute bottom-0 left-5 right-5 h-px bg-[#00D4FF]"
                    />
                  )}
                </Link>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="hidden lg:flex items-center gap-6">
              {/* Calendly Button */}
              <a
                href="https://calendly.com/excelleta-info/30min"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 bg-[#00D4FF] text-black font-semibold rounded-full hover:shadow-[0_0_30px_rgba(0,230,118,0.4)] transition-all"
                data-testid="calendly-btn"
              >
                <Calendar className="w-5 h-5" />
                Book a Demo
                <ArrowUpRight className="w-5 h-5" />
              </a>
              {/*<Link
                to="https://quote-alpha.costgenie.com/auth/login?returnUrl=%2Fdashboard"
                className="text-sm text-white/60 hover:text-white transition-colors tracking-wide"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                data-testid="nav-login-btn"
              >
                Sign In
              </Link>*/}
              {/* <Link
                to={REGISTER_URL}
                className="group px-4 py-2 bg-gradient-to-r from-purple-500 to-cyan-500 text-white font-medium rounded-xl hover:shadow-[0_0_50px_rgba(139,92,246,0.4)] transition-all flex items-center gap-2"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                data-testid="nav-get-started-btn"
              >
                Start Free Trial
              </Link> */}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-white"
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 lg:hidden"
            data-testid="mobile-menu"
          >
            <div
              className="absolute inset-0 bg-[#030508]/95 backdrop-blur-xl"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="absolute right-0 top-0 h-full w-80 max-w-full bg-[#030508] border-l border-white/5 pt-28 px-8"
            >
              <div className="flex flex-col gap-2">
                {navLinks.map((link, index) => (
                  <motion.div
                    key={link.name}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Link
                      to={link.href}
                      className={`block py-4 text-lg tracking-wide border-b border-white/5 transition-colors ${
                        location.pathname === link.href
                          ? "text-[#00D4FF]"
                          : "text-white/60 hover:text-white"
                      }`}
                      style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                      data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                    >
                      {link.name}
                    </Link>
                  </motion.div>
                ))}
              </div>

              <div className="mt-10 space-y-4">
                {/* <Link
                  to="/login"
                  className="block w-full py-4 text-center text-white/60 border border-white/10 tracking-wide uppercase text-sm"
                  style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                  data-testid="mobile-login-btn"
                >
                  Sign In
                </Link>*/}
                {/* <Link
                  to={REGISTER_URL}
                  className="block w-full py-4 text-center font-semibold bg-[#00D4FF] text-[#030508] tracking-wider uppercase text-sm"
                  style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                  data-testid="mobile-get-started-btn"
                >
                  Start Free Trial
                </Link> */}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
